import { supabase } from '../client';
import type { Database } from '../generated';

export type Membership = Database['core']['Tables']['user_memberships']['Row'];

export async function listMyMemberships(): Promise<Membership[]> {
  // RLS already restricts this read to the current user's own memberships.
  // Capping defensively at 500 prevents a runaway query if anyone ever
  // gives a single user that many org/store memberships.
  const { data, error } = await supabase()
    .schema('core')
    .from('user_memberships')
    .select('*')
    .limit(500);
  if (error) throw error;
  return data ?? [];
}

/**
 * True for a membership row that grants ORG-WIDE access: an org-scoped row
 * (`store_id IS NULL`) or an owner/admin (who are always org-wide, even if a
 * stray store-scoped row exists). Archived rows never count.
 *
 * This MIRRORS the server-side `core.user_has_org_wide_access(org_id)` gate
 * (migration `20260703160000_core_org_wide_access_gate.sql`). Keep the two in
 * sync: it is the single source of truth for "can this user enter org mode /
 * see cross-store aggregates". (Platform-admin org-wide access is server-only
 * and irrelevant here — apps/web only ever lists orgs the user is a member of.)
 */
export function membershipIsOrgWide(
  m: Pick<Membership, 'store_id' | 'role' | 'archived_at'>,
): boolean {
  if (m.archived_at) return false;
  return m.store_id === null || m.role === 'owner' || m.role === 'admin';
}

/** True when the user holds ORG-WIDE access to `orgId` among their rows. */
export function userHasOrgWideAccess(
  memberships: Membership[],
  orgId: string,
): boolean {
  return memberships.some(
    (m) => m.org_id === orgId && membershipIsOrgWide(m),
  );
}
